/**
 * Recovery code pages - replace allauth's mfa/recovery_codes/index.html and
 * mfa/recovery_codes/generate.html
 */
import { Box, Stack } from "@mui/material";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import {
  DangerButton,
  MFA_URLS,
  MfaBackLink,
  MfaContent,
  MfaMessages,
  MfaText,
  PostForm,
  PrimaryButton,
  SecondaryButton,
} from "../components/MfaParts";
import { useAuthContext } from "../hooks/useAuthContext";
import { gettext, interpolate } from "@services/i18n";

const TITLE = gettext("Recovery codes");
const CODES_INTRO = gettext(
  "Store these codes somewhere safe, e.g. in a password manager. If you lose access to your authenticator app, enter one of them instead of a code from the app. Each code works once.",
);
const RECOVERY_CODES_COUNT = gettext("%s of %s recovery codes are unused.");
const DOWNLOAD_CODES = gettext("Download codes");
const GENERATE_NEW_CODES = gettext("Generate new codes");
const GENERATE_TITLE = gettext("Generate new recovery codes");
const GENERATE_TEXT = gettext(
  "You are about to generate a new set of recovery codes for your account.",
);
const GENERATE_INVALIDATES = gettext("Your existing codes will stop working.");
const GENERATE = gettext("Generate");

export function RecoveryCodesPage() {
  const { recoveryCodes } = useAuthContext();
  const unusedCodes = recoveryCodes?.unusedCodes ?? [];

  return (
    <AuthLayout title={TITLE}>
      <MfaContent>
        <MfaMessages />
        <MfaText>{CODES_INTRO}</MfaText>
        {unusedCodes.length > 0 && (
          <Box
            component="ul"
            sx={(theme) => ({
              listStyle: "none",
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: theme.spacing(1, 3),
              margin: theme.spacing(0, 0, 2),
              padding: theme.spacing(2, 3),
              backgroundColor: "#F9FAFB",
              border: `1px solid ${BRAND_COLORS.border}`,
              borderRadius: 2,
              fontFamily: "monospace",
              fontSize: "1rem",
              letterSpacing: "0.08em",
              textAlign: "center",
              userSelect: "all",
            })}
            data-testid="mfa-recovery-codes-list"
          >
            {unusedCodes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </Box>
        )}
        {recoveryCodes?.totalCount !== undefined && (
          <MfaText>
            {interpolate(RECOVERY_CODES_COUNT, [
              recoveryCodes.unusedCount,
              recoveryCodes.totalCount,
            ])}
          </MfaText>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          {recoveryCodes?.canDownload && (
            <PrimaryButton
              href={MFA_URLS.downloadRecoveryCodes}
              data-testid="mfa-download-recovery-codes-button"
            >
              {DOWNLOAD_CODES}
            </PrimaryButton>
          )}
          {recoveryCodes?.canGenerate && (
            <SecondaryButton
              href={MFA_URLS.generateRecoveryCodes}
              data-testid="mfa-generate-recovery-codes-button"
            >
              {GENERATE_NEW_CODES}
            </SecondaryButton>
          )}
        </Stack>
        <MfaBackLink to="index" />
      </MfaContent>
    </AuthLayout>
  );
}

export function RecoveryCodesGeneratePage() {
  const { recoveryCodes, formErrors } = useAuthContext();
  const invalidatesExisting = (recoveryCodes?.unusedCount ?? 0) > 0;
  const SubmitButton = invalidatesExisting ? DangerButton : PrimaryButton;

  return (
    <AuthLayout title={GENERATE_TITLE}>
      <MfaContent>
        <MfaMessages />
        {Object.keys(formErrors).length === 0 && (
          <>
            <MfaText>
              {GENERATE_TEXT} {invalidatesExisting && GENERATE_INVALIDATES}
            </MfaText>
            <PostForm action={MFA_URLS.generateRecoveryCodes}>
              <SubmitButton
                type="submit"
                data-testid="mfa-generate-recovery-codes-submit"
              >
                {GENERATE}
              </SubmitButton>
            </PostForm>
          </>
        )}
        <MfaBackLink to="index" />
      </MfaContent>
    </AuthLayout>
  );
}
